import CalEmbed, { getCalApi } from '@calcom/embed-react';
import { useCallback, useEffect, useRef } from 'react';

import type { FC } from 'react';

interface CalEvent {
  detail: {
    type: string;
  };
}

interface CalProps {
  calLink: string;
  callback?: () => void;
  email?: string;
  name?: string;
  newCalendarsEnabled?: boolean;
}

const CAL_EMBED_NAMESPACE = 'mento-member-scheduling';
const NOOP = () => {};

const getEmbedJsUrl = (calOrigin: string) =>
  `${calOrigin.replace(/\/$/u, '')}/embed/embed.js`;

const Cal: FC<CalProps> = ({
  calLink,
  callback = NOOP,
  email = '',
  name = '',
  newCalendarsEnabled = false,
}) => {
  const calOrigin = import.meta.env.VITE_CAL_URL;
  const embedJsUrl = calOrigin ? getEmbedJsUrl(calOrigin) : undefined;
  const isLoadedRef = useRef(false);

  const onCallback = useCallback(
    (e: CalEvent) => {
      if (e.detail.type === '__windowLoadComplete') {
        isLoadedRef.current = true;
      } else if (isLoadedRef.current && e.detail.type === 'bookingSuccessful') {
        callback();
      }
    },
    [callback],
  );

  useEffect(() => {
    if (!embedJsUrl) return undefined;

    let isActive = true;

    const handler = async () => {
      const api = await getCalApi({
        embedJsUrl,
        namespace: CAL_EMBED_NAMESPACE,
      });

      if (!isActive) return;

      api('on', {
        action: '*',
        callback: onCallback,
      });
    };

    handler();

    return () => {
      isActive = false;

      getCalApi({ embedJsUrl, namespace: CAL_EMBED_NAMESPACE }).then((api) => {
        api('off', {
          action: '*',
          callback: onCallback,
        });
      });
    };
  }, [embedJsUrl, onCallback]);

  return calOrigin && embedJsUrl ? (
    <CalEmbed
      calLink={calLink}
      calOrigin={calOrigin}
      className="z-0 h-screen bg-sunny-100"
      config={{
        email,
        metadata: {
          newCalendarsEnabled: newCalendarsEnabled ? 'true' : 'false',
        },
        name,
        shouldDisableInput: 'true',
      }}
      embedJsUrl={embedJsUrl}
      namespace={CAL_EMBED_NAMESPACE}
    />
  ) : (
    <div className="text-red-500">Cal.com url not set</div>
  );
};

export default Cal;
